"use client";

import Image from "next/image";
import { motion } from "motion/react";
import { Button } from "@heroui/react";
import { ArrowRight, Sparkles } from "lucide-react";
import MacWindowMockup from "./MacWindowMockup";
import { SCHEDULE_DEMO_URL } from "@/lib/site-config";

const fadeUp = (delay: number, extraY = 24) => ({
  initial: { opacity: 0, y: extraY },
  animate: { opacity: 1, y: 0 },
  transition: {
    duration: 0.6,
    delay,
    ease: [0.25, 0.1, 0.25, 1] as const,
  },
});

const mockupVariant = {
  initial: { opacity: 0, y: 32, scale: 0.97 },
  animate: { opacity: 1, y: 0, scale: 1 },
  transition: {
    duration: 0.8,
    delay: 0.4,
    ease: [0.25, 0.1, 0.25, 1] as const,
  },
};

export default function HeroSection() {
  return (
    <section
      className="relative mx-auto mt-8 sm:mt-10 lg:mt-2 w-[calc(100%-2rem)] sm:w-[calc(100%-3rem)] lg:w-[calc(100%-2rem)] max-w-[1480px] overflow-hidden rounded-[28px] sm:rounded-[32px] lg:rounded-[48px] rounded-b-none sm:rounded-b-none lg:rounded-b-none border border-gray-200"
      style={{
        maskImage: "linear-gradient(to bottom, black 0%, black 73%, transparent 100%)",
        WebkitMaskImage: "linear-gradient(to bottom, black 0%, black 73%, transparent 100%)",
      }}
    >
      {/* Blurred colorful background image */}
      <Image
        src="/templates/hero-bg.webp"
        alt=""
        fill
        priority
        sizes="(max-width: 768px) 100vw, 1240px"
        className="object-cover object-center pointer-events-none select-none"
      />

      {/* Top white-to-transparent wash */}
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_bottom,rgb(255,255,255)_-5.72%,rgba(255,255,255,0)_39.08%)]" />

      {/* Content layer */}
      <div className="relative z-10 flex flex-col items-center px-6 sm:px-10 lg:px-14 pt-14 sm:pt-16 md:pt-19 lg:pt-20 pb-16 sm:pb-20">
        {/* Launch badge — Veri */}
        <motion.a
          {...fadeUp(0.05, 20)}
          href="#copilot"
          className="group mb-5 inline-flex max-w-full items-center gap-2 whitespace-nowrap rounded-full border border-white bg-white/60 px-3.5 py-1.5 text-[13px] font-medium text-dark-teal/70 backdrop-blur-sm ring-1 ring-dark-teal/[0.04] transition-all hover:ring-accent/30 hover:text-dark-teal"
        >
          <Sparkles className="h-3.5 w-3.5 text-accent" strokeWidth={2} />
          <span>
            <span className="font-semibold text-accent">Nuevo</span> ·{" "}
            <span className="hidden sm:inline">Conoce a </span>Veri, tu copiloto
            con IA
          </span>
          <ArrowRight
            className="h-3.5 w-3.5 text-dark-teal/35 transition-transform group-hover:translate-x-0.5"
            strokeWidth={2}
          />
        </motion.a>

        {/* Heading */}
        <motion.h1
          {...fadeUp(0.1, 30)}
          className="max-w-[790px] text-balance text-center text-[2.25rem] sm:text-[48px] lg:text-5xl leading-[1.12] lg:leading-[1.03] font-medium tracking-[-0.025em] text-zinc-900 mb-5"
        >
          Cumplimiento de contratistas,<br />
          <span className="bg-linear-to-r from-[#4A66D9] to-[#3F8E9D] bg-clip-text text-transparent font-medium">bajo control.</span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          {...fadeUp(0.2)}
          className="max-w-[360px] sm:max-w-[540px] text-balance text-center text-[16px] sm:text-[18px] leading-[1.6] text-black font-normal mb-8 sm:mb-10"
        >
          Centraliza documentos, recibe alertas de vencimiento y pregúntale a
          Veri el estado de cada proveedor, en tiempo real.
        </motion.p>

        {/* CTAs */}
        <motion.div
          {...fadeUp(0.3)}
          className="mb-12 flex w-full max-w-[340px] flex-col items-center gap-3 sm:mb-16 sm:w-auto sm:max-w-none sm:flex-row"
        >
          <Button
            onPress={() => {
              window.open(SCHEDULE_DEMO_URL, "_blank", "noopener,noreferrer");
            }}
            className="btn-glow flex h-11 w-full items-center justify-center gap-3 rounded-2xl bg-accent px-6 text-[14px] font-semibold tracking-[-0.01em] text-white hover:bg-[#5570e0] sm:w-auto"
          >
            Agendar una demo
            <ArrowRight className="w-4 h-4 text-white/80" strokeWidth={2} />
          </Button>
          <Button
            onPress={() =>
              document
                .getElementById("copilot")
                ?.scrollIntoView({ behavior: "smooth" })
            }
            className="h-11 w-full rounded-2xl border border-dark-teal/12 bg-white px-6 text-[14px] sm:w-auto font-medium tracking-[-0.01em] text-dark-teal/75 transition-colors hover:border-accent/40 hover:bg-white hover:text-dark-teal"
          >
            Conocer a Veri
          </Button>
        </motion.div>

        {/* Mac Window Mockup with bottom fade */}
        <motion.div
          {...mockupVariant}
          className="relative w-full max-w-[1100px]"
        >
          <MacWindowMockup />
        </motion.div>
      </div>
    </section>
  );
}
